import { useState, useEffect, useRef, useCallback } from "react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { Network, RefreshCw, Zap } from "lucide-react";
import { apiService } from "@/lib/api-functions/api-service";
import { EntityRelationship } from "@/components/graphs/EntityRelationshipGraph";
import { DrillDownRelationshipGraph } from "@/components/graphs/DrillDownRelationshipGraph";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks";
import { confirmation } from "@/hooks/use-confirmation";
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';
import { useZoom } from '@/providers/ZoomProvider';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export default function RelationshipPage() {
  usePageAccessGuard('relationships');
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const { zoom } = useZoom();
  const [relationships, setRelationships] = useState<EntityRelationship[]>([]);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Calculate inverse zoom to counteract parent's CSS zoom
  const inverseZoom = 100 / zoom;

  // Fetch relationships
  const fetchRelationships = useCallback(async () => {
    if (!selectedGame) return;

    setLoading(true);
    setError(null);

    try {
      const result = await apiService.getAllGameRelationships(selectedGame.id);
      
      if (result.success && result.data) {
        setRelationships(result.data);
      } else {
        setError(result.error || 'Failed to fetch relationships');
      }
    } catch (err) {
      console.error('Error fetching relationships:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedGame]);

  // Rebuild all relationships
  const handleRebuild = useCallback(async () => {
    if (!selectedGame) return;

    // Show custom confirmation dialog
    const dialog = confirmation({
      title: "Rebuild All Relationships",
      description: (
        <div className="space-y-3">
          <p>
            This will rebuild all relationships for <strong>{selectedGame.name}</strong>.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-2">
              This action will:
            </p>
            <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
              <li>{t('platform.pages.relationshippage.process_all_library_objects_and_documents')}</li>
              <li>{t('platform.pages.relationshippage.clean_up_orphaned_relationships')}</li>
              <li>{t('platform.pages.relationshippage.recreate_all_entity_relationships')}</li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            This may take a few moments depending on the size of your game.
          </p>
        </div>
      ),
      confirmText: "Rebuild All",
      cancelText: "Cancel",
      variant: "default",
      onConfirm: async () => {
        dialog.dismiss();
        setRebuilding(true);
        toast({
          title: "Rebuilding relationships...",
          description: "This may take a few moments",
        });

        try {
          const result = await apiService.rebuildGameRelationships(selectedGame.id);
          
          if (result.success && result.data) {
            const cleanedCount = result.data.cleaned || 0;
            const cleanupMessage = cleanedCount > 0 
              ? ` Cleaned ${cleanedCount} orphaned relationship${cleanedCount !== 1 ? 's' : ''}.`
              : '';
            
            toast({
              title: "Relationships rebuilt successfully",
              description: `Processed ${result.data.totalProcessed} items (${result.data.totalErrors} errors).${cleanupMessage}`,
            });
            
            // Refresh the graph
            await fetchRelationships();
          } else {
            toast({
              title: "Failed to rebuild relationships",
              description: result.error || 'An error occurred',
              variant: "destructive",
            });
          }
        } catch (err) {
          console.error('Error rebuilding relationships:', err);
          toast({
            title: "Error",
            description: err instanceof Error ? err.message : 'An error occurred',
            variant: "destructive",
          });
        } finally {
          setRebuilding(false);
        }
      },
      onCancel: () => {
        // User cancelled - dismiss the dialog
        dialog.dismiss();
      }
    });
  }, [selectedGame, fetchRelationships, toast, t]);

  // Update dimensions on resize using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // getBoundingClientRect returns dimensions before CSS zoom is applied
        // We store these raw dimensions and adjust for zoom when passing to the graph
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // Initial update with a small delay to ensure layout is settled
    const timeoutId = setTimeout(updateDimensions, 100);

    // Use ResizeObserver for more accurate container size tracking
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [zoom]);

  // Fetch on mount and when game changes
  useEffect(() => {
    if (selectedGame) {
      fetchRelationships();
    }
  }, [selectedGame, fetchRelationships]);

  if (!selectedGame) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('platform.pages.relationshippage.please_select_a_game_to_view_relationships')}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border ">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Network className="w-6 h-6 text-muted-foreground" />
              <div className="flex flex-row gap-4 mt-3">
                <h3 className="text-xl font-semibold text-foreground">
                  Entity Relationships
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedGame.name} • {relationships.length} relationships
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRebuild}
                disabled={loading || rebuilding}
                variant="default"
                size="sm"
              >
                <Zap className={`w-4 h-4 mr-2 ${rebuilding ? 'animate-pulse' : ''}`} />
                Rebuild All
              </Button>
              <Button
                onClick={fetchRelationships}
                disabled={loading || rebuilding}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {t('platform.common.refresh')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden relative">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner size="lg" text="Loading relationships..." />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-destructive text-lg mb-2">{t('platform.pages.relationshippage.error_loading_relationships')}</p>
              <p className="text-muted-foreground text-sm mb-4">{error}</p>
              <Button onClick={fetchRelationships} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        ) : dimensions.width > 0 && dimensions.height > 0 ? (
          <div 
            className="absolute inset-0"
            style={{ zoom: `${inverseZoom}%` }}
          >
            <DrillDownRelationshipGraph
              relationships={relationships}
              width={dimensions.width}
              height={dimensions.height}
              enableDrillDown={true}
              disableZoomCompensation={true}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner size="lg" text="Preparing graph..." />
          </div>
        )}
      </div>
    </div>
  );
}

